﻿import {Core} from "../Base/Core";
import {Price} from "./GameData";
import {BoosterType, MoneyType, SpinOfferType, UpgradeType} from "../enums";
import {JSONGameData} from "./JSONGameData";
import {PlayerPrefs} from "../Utils/Unity/PlayerPrefs";

const GLOBAL_VERSION_VALUE = 76;

export class PlayerData extends JSONGameData {
    static get GLOBAL_VERSION_VALUE() {
        return GLOBAL_VERSION_VALUE;
    }

    _changed = false;

    // public int MaxSpinsBonus
    get MaxSpinsBonus() {
        return this.PlanetsSave.MaxSpinsBonus;
    }

    set MaxSpinsBonus(value) {
        this.PlanetsSave.MaxSpinsBonus = value;
    }

    // public int SpinsUsed
    get SpinsUsed() {
        return this.PlanetsSave.SpinsUsed;
    }

    set SpinsUsed(value) {
        this.PlanetsSave.SpinsUsed = value;
    }

    // public SpinOfferType SpinsOfferType
    get SpinsOfferType() {
        return this.PlanetsSave.SpinsOfferType;
    }

    set SpinsOfferType(value) {
        this.PlanetsSave.SpinsOfferType = value;
    }

    //    [JsonIgnore]
    get MaxSpinsNumber() {
        return this.PlanetsSave.MaxSpinsBonus + Core.GameData.Slots.spinsMaxCount;
    }

    get MoneySoftStr() {
        return this.MoneySoft.toString();
    }

    set MoneySoftStr(val) {
        this.MoneySoft = BigInt(val || 0);
    }

    get MoneyHardStr() {
        return this.MoneyHard.toString();
    }

    set MoneyHardStr(val) {
        this.MoneyHard = BigInt(val || 0);
    }

    get CollectionCreatures() {
        let res = new Set();
        for (let planet of this.PlanetsSave.planets) {
            for (let creature of planet.collection) {
                res.add(creature);
            }
        }
        return [...res];
    }

    Awake() {
        super.Awake();

        this.Initialized = false;
        this.DefaultData = false;
        this.MoneySoft = 0n;
        this.MoneyHard = 0n;
        this.SpinsNumber = 0;
        this.Missions = new MissionsSave();
        this.Timers = new PlayerTimers();
        this.PlanetsSave = new PlanetsSave();
        this.TapChallengeSave = new TapChallengeSave(); // now this only saves locally
        this.NoAds = false;
        this.StarterPack = 0; // int
        this.Version = 0; // long
        this.GlobalVersion = 0; // int
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.Missions = new MissionsSave(this.Missions); // [JsonIgnore]
        this.Timers = new PlayerTimers(this.Timers); // [JsonIgnore]
        this.PlanetsSave = new PlanetsSave(this.PlanetsSave);
        this.TapChallengeSave = new TapChallengeSave(this.TapChallengeSave); // now this only saves locally
        this.MoneySoft = BigInt(json.MoneySoftStr || 0);
        this.MoneyHard = BigInt(json.MoneyHardStr || 0);
        this.Initialized = true;
        this.NoAds = !!json.NoAds;
    }

    toJSON() {
        return {
            NoAds: !!this.NoAds,
            MoneySoftStr: this.MoneySoftStr,
            MoneyHardStr: this.MoneyHardStr,
            SpinsNumber: this.SpinsNumber,
            Missions: this.Missions.toJSON(),
            Timers: this.Timers.toJSON(),
            PlanetsSave: this.PlanetsSave.toJSON(),
            TapChallengeSave: this.TapChallengeSave.toJSON(),
            StarterPack: this.StarterPack,
            // Version: this.Version,
            Version: this.GlobalVersion, // XXX
            GlobalVersion: this.GlobalVersion
        };
    }

    DefaultStart() {
        Core.DeleteAllPlayerPrefs();

        const DEFAULT_START = {
            DefaultData: true,
            PlanetsSave: {}, //new PlanetsSave(),
            Timers: {}, //new PlayerTimers(),
            Missions: {}, //new MissionsSave(),
            TapChallengeSave: {}, //new TapChallengeSave(),
            MoneySoftStr: "0",
            MoneyHardStr: "0",
            SpinsNumber: this.MaxSpinsNumber,
            NoAds: 0,
            StarterPack: 0,
            Version: 0,
            GlobalVersion: GLOBAL_VERSION_VALUE
        };

        this.Init(DEFAULT_START);

        this.PlanetsSave.MaxSpinsBonus = 0;
        this.PlanetsSave.AddPlanet(1, false);
        this.PlanetsSave.SetCurrentPlanet(1, false);
    }

    CopyPlayerFrom(player) {
        this.Init(player);
    }

    ClearTimers() {
        if (!this.Timers) return;
        if (!this.Timers.timers) return;

        for (let i = this.Timers.timers.length - 1; i >= 0; i--) {
            let timer = this.Timers.timers[i];
            if (timer.IsFinished) {
                let used =
                    this.Missions &&
                    this.Missions.missionInProgress &&
                    this.Missions.missionInProgress.timerId == timer.tId;
                if (!used) {
                    this.Timers.timers.splice(i, 1);
                }
            }
        }
    }

    CallOnMergeBonus() {
        this.emit("MergeBonus");
    }

    CallOnChanged() {
        this.Version++;
        this._changed = true;
    }

    EmitOnChanged() {
        if (this._changed) {
            this.emit("Changed");
        }
        this._changed = false;
    }

    CallOnNewCreatureInCollection(creatureId) {
        this.emit("NewCreatureInCollection", creatureId);
    }

    AddMoney(type, number, category = "", screen = "", itemId = "") {
        let analytics = category && screen && itemId;
        number = BigInt(~~number);
        switch (type) {
            case MoneyType.Hard:
                console.log("ADD DIAMONDS", number);
                this.MoneyHard += number;
                if (analytics) {
                    Core.Analytics.SendHardEvent(category, screen, itemId, Number(number));
                }
                break;
            case MoneyType.Soft:
                this.MoneySoft += number;
                break;
            case MoneyType.Spins:
                console.log("ADD SPINS", number);
                this.SpinsNumber = Math.max(
                    0,
                    Math.min(this.MaxSpinsNumber, this.SpinsNumber + Number(number))
                );
                break;
        }

        this.CallOnChanged();
    }

    EnoughMoney(type, number) {
        if (typeof type == "object" && type.priceValue) {
            number = type.priceValue;
            type = type.priceType;
        }

        switch (type) {
            case MoneyType.Hard:
                return this.MoneyHard >= number;

            case MoneyType.Soft:
                return this.MoneySoft >= number;

            case MoneyType.Spins:
                return this.SpinsNumber >= number;
        }

        return false;
    }

    SpendMoney(price, ...args) {
        let type = price;
        if (typeof price == "object" && price && price.priceValue) {
            args.unshift(price.priceValue); // fake argument
            type = price.priceType;
        }
        let [number, category = "", screen = "", itemId = ""] = args;
        console.log("SPEND:", type, number, category, screen, itemId);

        if (!this.EnoughMoney(type, number)) return false;

        number = BigInt(~~number);
        let analytics = category && screen && itemId;
        switch (type) {
            case MoneyType.Hard:
                this.MoneyHard -= number;
                if (analytics) {
                    Core.Analytics.SendHardEvent(category, screen, itemId, -Number(number));
                }
                break;
            case MoneyType.Soft:
                this.MoneySoft -= number;
                if (analytics) {
                    Core.Analytics.SendSoftEvent(category, screen, itemId, Number(number));
                }
                break;
            case MoneyType.Spins:
                if (this.SpinsNumber >= Core.Player.MaxSpinsNumber)
                    Core.Player.Timers.LastSpinsRestoreTicks = Core.Time.serverTime;
                this.SpinsNumber -= Number(number);
                break;
        }
        this.CallOnChanged();
        return true;
    }

    IsEpochLocked(epochId) {
        let nextEpochs = this.getNextEpochs(epochId);
        let locked = true;
        for (let ep of nextEpochs) {
            let found = false;
            for (let p of Core.Player.PlanetsSave.planets) {
                if (p.epochs.includes(ep)) found = true;
            }
            if (!found) locked = false;
        }
        return locked;
    }

    getNextEpochs(epochId, epochs = new Set()) {
        let epoch = Core.GameData.Epochs.GetById(epochId);
        if (epoch.nextEpochs) {
            for (let e of epoch.nextEpochs) {
                epochs.add(e);
                this.getNextEpochs(e, epochs);
            }
        }
        return [...epochs];
    }
}

export class PlayerTimers extends JSONGameData {
    get registrationTime() {
        return this.t0;
    }

    set registrationTime(value) {
        this.t0 = value;
    }

    get lastActionTime() {
        return this.t1;
    }

    set lastActionTime(value) {
        this.t1 = value;
    }

    get starterPackTime() {
        return this.t2;
    }

    set starterPackTime(value) {
        this.t2 = value;
    }

    get firstCreatureOfferTime() {
        return this.t3;
    }

    set firstCreatureOfferTime(value) {
        this.t3 = value;
    }

    get spinsOfferTime() {
        return this.t4;
    }

    set spinsOfferTime(value) {
        this.t4 = value;
    }

    get RestoreSpinsByAdsDay() {
        return this.sdid;
    }

    set RestoreSpinsByAdsDay(value) {
        this.sdid = value;
    }

    get SlotsOpened() {
        return !!this.s;
    }

    set SlotsOpened(value) {
        this.s = ~~!!value;
    }

    get LastSpinsRestoreTicks() {
        return this.lsr;
    }

    set LastSpinsRestoreTicks(value) {
        this.lsr = value;
    }

    get TicksFromLastSpinsRestore() {
        return Core.Time.serverTime - this.lsr;
    }

    Awake() {
        super.Awake();
        this.nextTimerId = 0;
        this.t0 = 0; // registration
        this.t1 = 0; // last action
        this.t2 = 0; // starter pack
        this.t3 = 0; // creature offer
        this.t4 = 0; // spins offer
        this.ts = 0;
        this.ir = 0;
        this.sdid = 0;
        this.s = 0;
        this.sb = 0;
        this.st = 0;
        this.lt = 0;
        this.sn = 0;
        this.vipDailyRewards = [];
        this.vipActivationTime = 0;
        this.l4b = 0; // last taken x4 booster
        this.l2b = 0; // last taken x2 booster
        this.lsr = 0; // last spins restore
        this.incomeBoosterTimerId = 0;
        this.incomeX4BoosterTimerId = 0;
        this.discountBoosterTimerId = 0;
        this.bubbleBoosterTimerId = 0;
        this.timers = []; // List<PlayerTimer>
        this.timersById = new Map(); //Dictionary<int, PlayerTimer>();
    }

    fromJSON(json) {
        super.fromJSON(json);
        this.timers = this.timers.map(data => new PlayerTimer(data));
    }

    toJSON() {
        return {
            nextTimerId: this.nextTimerId,
            t0: this.t0,
            t1: this.t1,
            t2: this.t2,
            t3: this.t3,
            t4: this.t4,
            ts: this.ts,
            ir: this.ir,
            sdid: this.sdid,
            s: this.s,
            sb: this.sb,
            st: this.st,
            lt: this.lt,
            sn: this.sn,
            vipDailyRewards: this.vipDailyRewards,
            vipActivationTime: this.vipActivationTime,
            l4b: this.l4b,
            l2b: this.l2b,
            lsr: this.lsr,
            incomeBoosterTimerId: this.incomeBoosterTimerId,
            incomeX4BoosterTimerId: this.incomeX4BoosterTimerId,
            discountBoosterTimerId: this.discountBoosterTimerId,
            bubbleBoosterTimerId: this.bubbleBoosterTimerId,
            timers: this.timers.map(obj => obj.toJSON())
        };
    }

    OnVipTaken() {
        this.vipDailyRewards = [Core.Time.DayUniqId];
        Core.Player.CallOnChanged();
    }

    IsVipTakenToday() {
        if (!this.vipDailyRewards) return false;
        if (!Core.Time.Initialized) return false;
        let purchaseDay = Core.Time.TicksToDayId(this.vipActivationTime);
        return this.vipDailyRewards.includes(Core.Time.DayUniqId);
    }

    AddTimer(seconds = 0) {
        Core.Player.ClearTimers();
        let newTimer = new PlayerTimer();
        newTimer.tId = ++this.nextTimerId;
        newTimer.tTargetSeconds = newTimer.tSeconds = seconds;
        this.timers.push(newTimer);
        this.timersById.set(newTimer.tId, newTimer);
        Core.Player.CallOnChanged();
        return newTimer;
    }

    RemoveTimer(id) {
        this.timers = this.timers.filter(t => t.tId != id);
        this.timersById.delete(id);
    }

    GetTimerById(id) {
        if (this.timersById.has(id)) {
            return this.timersById.get(id);
        }
        for (let t of this.timers) {
            if (t.tId == id) return t;
        }
        return null;
    }

    TimeReset() {
        for (let timer of this.timers) {
            if (!timer.IsFinished) {
                timer.tSeconds = timer.tTargetSeconds = 60 * 2;
                timer.tMinusSeconds = 0;
            }
        }
    }

    GetBoosterTimer(boosterType) {
        switch (boosterType) {
            case BoosterType.BubbleTime:
                return this.GetTimerById(this.bubbleBoosterTimerId);

            case BoosterType.CreaturesDiscount:
                return this.GetTimerById(this.discountBoosterTimerId);

            case BoosterType.Income:
                return this.GetTimerById(this.incomeBoosterTimerId);

            case BoosterType.IncomeX4:
                return this.GetTimerById(this.incomeX4BoosterTimerId);
        }
    }

    AddBoosterTimer(seconds, boosterType) {
        let timer = this.AddTimer(seconds);
        switch (boosterType) {
            case BoosterType.BubbleTime:
                this.bubbleBoosterTimerId = timer.tId;
                break;

            case BoosterType.CreaturesDiscount:
                this.discountBoosterTimerId = timer.tId;
                break;

            case BoosterType.Income:
                this.incomeBoosterTimerId = timer.tId;
                break;

            case BoosterType.IncomeX4:
                this.incomeX4BoosterTimerId = timer.tId;
                break;
        }
        return timer;
    }
}

export class PlayerTimer extends JSONGameData {
    get IsFinished() {
        return this.SecondsLeft <= 0;
    }

    get SecondsLeft() {
        return ~~this.tSeconds - ~~this.tMinusSeconds;
    }

    get TicksLeft() {
        return this.SecondsLeft * this.ticksInSecond;
    }

    AddSeconds(seconds) {
        this.tTargetSeconds += seconds;
        this.tSeconds += seconds;
        Core.Player.CallOnChanged();
    }

    MinusSeconds(seconds) {
        this.tMinusSeconds += seconds;
        Core.Player.CallOnChanged();
    }

    InitializeStartTime(tStart) {
        if (!this.tStart) {
            this.lastTUpdate = tStart;
            tStart -= (this.tTargetSeconds - this.tSeconds) * this.ticksInSecond;
            this.tStart = tStart;
        }
        Core.Player.CallOnChanged();
    }

    StepServerTime(currTicks) {
        this.lastTUpdate = currTicks;
        this.tSeconds = this.tTargetSeconds - (currTicks - this.tStart) / 1000;
        Core.Player.CallOnChanged();
    }

    Step(tStep) {
        this.tSeconds -= tStep;
        Core.Player.CallOnChanged();
    }

    Awake() {
        super.Awake();
        this.ticksInSecond = 10000000; // XXX
        this.tId = 0;
        this.tTargetSeconds = 0;
        this.tSeconds = 0;
        this.tStart = 0;
        this.lastTUpdate = 0;
        this.tMinusSeconds = 0;
    }

    toJSON() {
        return {
            tId: this.tId,
            tTargetSeconds: this.tTargetSeconds,
            tSeconds: this.tSeconds,
            tStart: this.tStart,
            lastTUpdate: this.lastTUpdate,
            tMinusSeconds: this.tMinusSeconds
        };
    }
}

export class TapChallengeSave extends JSONGameData {
    Awake() {
        super.Awake();
        this.challengesLostCount = 0;
        this.challengesWinedCount = 0;
        this.challengesStarted = 0;
    }

    toJSON() {
        return {
            challengesLostCount: this.challengesLostCount,
            challengesWinedCount: this.challengesWinedCount,
            challengesStarted: this.challengesStarted
        };
    }

    ChallengeStart() {
        this.challengesStarted++;
    }

    ChallengeWin() {
        this.challengesWinedCount++;
    }

    ChallengeLost() {
        this.challengesLostCount++;
    }
}

export class MissionsSave extends JSONGameData {
    Awake() {
        super.Awake();
        this.missionInProgress = null; // MissionSave
        this.nextMissionSavedId = 0;
        this.tutorialMissionComplete = 0;
    }

    fromJSON(json) {
        super.fromJSON(json);
        if (this.missionInProgress) {
            this.missionInProgress = new MissionSave(this.missionInProgress);
        }
    }

    toJSON() {
        return {
            missionInProgress: this.missionInProgress ? this.missionInProgress.toJSON() : null,
            nextMissionSavedId: this.nextMissionSavedId,
            tutorialMissionComplete: this.tutorialMissionComplete
        };
    }

    IsTutorial() {
        return this.tutorialMissionComplete == 0;
    }

    HasActiveMission() {
        return this.missionInProgress && this.missionInProgress.missionSaveId;
    }

    IsInProgress() {
        if (!this.HasActiveMission()) return false;
        let timer = Core.Player.Timers.GetTimerById(this.missionInProgress.timerId);
        return timer && !timer.IsFinished;
    }

    CreateMission(missionId) {
        let missionData = Core.GameData.Missions.GetMission(missionId);
        let missionSave = new MissionSave();
        missionSave.missionSaveId = ++this.nextMissionSavedId;
        missionSave.missionId = missionId;
        missionSave.timerId = Core.Player.Timers.AddTimer(missionData.durationSeconds).tId;
        this.missionInProgress = missionSave;
        Core.Player.CallOnChanged();
    }

    FinishMission() {
        this.missionInProgress = null;
        this.tutorialMissionComplete = 1;
        Core.Player.CallOnChanged();
    }

    MinusTime(seconds) {
        if (this.HasActiveMission()) {
            var timer = Core.Player.Timers.GetTimerById(this.missionInProgress.timerId);
            if (timer) {
                timer.MinusSeconds(seconds);
                Core.Player.CallOnChanged();
            }
        }
    }
}

export class MissionSave extends JSONGameData {
    Awake() {
        super.Awake();
        this.missionSaveId = 0;
        this.missionId = 0;
        this.timerId = 0;
    }

    toJSON() {
        return {
            missionSaveId: this.missionSaveId,
            missionId: this.missionId,
            timerId: this.timerId
        };
    }
}

export class TutorSave extends JSONGameData {
    get step() {
        if (this.bubbles < 3) return this.bubbles;
        if (!this.merged) return 3;
        if (!this.creatures) return 4;
        if (!this.creature) return 5;
        if (!this.spins) return 6;
        if (!this.spin) return 7;
        if (!this.map) return 8;
        if (!this.missions) return 9;
        if (!this.mission) return 10;
        return NaN;
    }

    Awake() {
        super.Awake();
        this.bubbles = 0;
        this.merged = false;
        this.creatures = false;
        this.creature = false;
        this.spin = false;
        this.map = false;
        this.missions = false;
        this.mission = false;
    }

    toJSON() {
        return {
            bubbles: this.bubbles,
            merged: this.merged,
            creatures: this.creatures,
            creature: this.creature,
            spins: this.spins,
            spin: this.spin,
            map: this.map,
            missions: this.missions,
            mission: this.mission
        };
    }
}

export class PlanetsSave extends JSONGameData {
    get LastNotEventPlanet() {
        return this.LastPlanet;
    }

    get LastPlanet() {
        return this.planets[this.planets.length - 1];
    }

    get CurrentPlanet() {
        return this.GetPlanet(this.currentPlanetId);
    }

    get openedEpochs() {
        let res = new Set();
        for (let planet of this.planets) {
            for (let epochId of planet.epochs) {
                res.add(epochId);
            }
        }
        return [...res];
    }

    Awake() {
        super.Awake();
        this.planets = []; // List<PlanetSave>
        this.collection = []; // List<int>
        this.completedEvents = []; // List<int>
        this.currentPlanetId = 0;
        this.TutorSave = new TutorSave();
        this.MaxSpinsBonus = 0;
        this.SpinsUsed = 0;
        this.SpinsOfferType = SpinOfferType.FirstLevel1; // SpinsOfferType
        this.planetsDic = new Map(); // Dictionary<int, PlanetSave>
    }

    fromJSON(json) {
        super.fromJSON(json);

        this.planets = (this.planets || []).map(data => new PlanetSave(data));
        this.TutorSave = new TutorSave(this.TutorSave);

        for (let p of this.planets) {
            this.planetsDic.set(p.planetId, p);
        }
        if (this.currentPlanetId > 100) {
            this.currentPlanetId = 1;
        }
    }

    toJSON() {
        return {
            planets: this.planets.map(obj => obj.toJSON()),
            collection: this.collection,
            completedEvents: this.completedEvents,
            currentPlanetId: this.currentPlanetId,
            TutorSave: this.TutorSave.toJSON(),
            MaxSpinsBonus: this.MaxSpinsBonus,
            SpinsUsed: this.SpinsUsed,
            SpinsOfferType: this.SpinsOfferType
        };
    }

    AddBubble() {
        this.CurrentPlanet.bubbles++;
        Core.Player.CallOnChanged();
    }

    RemoveCreature(id) {
        let idx = this.CurrentPlanet.creatures.indexOf(id);
        if (idx >= 0) {
            this.CurrentPlanet.creatures.splice(idx, 1);
        }
        Core.Player.CallOnChanged();
    }

    RemoveBubble() {
        this.CurrentPlanet.bubbles--;
        Core.Player.CallOnChanged();
    }

    GetPlanet(id) {
        if (this.planetsDic) {
            if (this.planetsDic.has(id)) {
                return this.planetsDic.get(id);
            }
        }

        if (this.planets) {
            for (let p of this.planets) {
                if (p.planetId === id) {
                    return p;
                }
            }
        }

        return null;
    }

    AddPlanet(id, callEvent = true, startEpoch = 1) {
        if (this.planetsDic.has(id)) return;
        for (let p of this.planets) if (p.planetId === id) return;

        let newPlanet = new PlanetSave({planetId: id}, startEpoch);
        this.planets.push(newPlanet);
        this.planetsDic.set(id, newPlanet);

        Core.Analytics.SendPlanetUnlockEvent(id);

        if (callEvent) {
            if (Core.Player) Core.Player.CallOnChanged();
        }
    }

    RemovePlanet(id, callEvent = true) {
        // to prevent removing first planet
        if (!planetsDic.ContainsKey(id) || id == 0) return;

        let index = -1;
        for (let i = 0; i < this.planets.length; i++) {
            if (this.planets[i].planetId === id) {
                index = i;
                break;
            }
        }

        Core.WaitBefore(1, () => {
            if (index >= 0) {
                this.planetsDic.delete(id);
                this.planets.splice(index, 1);
                if (callEvent) {
                    if (Core.Player) Core.Player.CallOnChanged();
                }
            }
        });
    }

    SetCurrentPlanet(id, callEvent = true) {
        if (this.CurrentPlanet) {
            this.CurrentPlanet.off("EpochUnlocked");
        }

        this.currentPlanetId = id;
        Core.Player.emit("CurrentPlanetChanged");
        if (callEvent) {
            Core.Player.CallOnChanged();
        }

        Core.UI.skipEpochsOffer.Check();
        Core.UI.game.rightPanel.skipEpochsOfferBtn.Check();

        this.CurrentPlanet.on("EpochUnlocked", epochId => this.emit("EpochUnlocked", epochId));
    }

    HasPlanet(id) {
        for (let p of this.planets) {
            if (p.planetId === id) return true;
        }
        return false;
    }

    GetUpgradeProgress(upgradeType) {
        return this.CurrentPlanet.GetUpgradeProgress(upgradeType);
    }

    Upgrade(upgradeType, level = undefined) {
        this.CurrentPlanet.Upgrade(upgradeType, level);
    }

    EpochCanBeChosen(epochId) {
        let epochData = Core.GameData.Epochs.GetById(epochId);
        if (!epochData) return false;

        let openedEpochs = this.openedEpochs;
        if (openedEpochs.includes(epochData.id)) {
            // If opened before, at least one of next epochs should be available
            for (let id of epochData.nextEpochs) {
                if (this.EpochCanBeChosen(id)) return true;
            }
        } else {
            // Not opened before
            return true;
        }

        // None of conditions have met
        return false;
    }
}

export class PlanetSave extends JSONGameData {
    constructor(json, startEpochId = 1) {
        super(json);
        if (!this.epochs.includes(startEpochId)) {
            this.epochs.push(startEpochId);
        }
        Core.Analytics.SendEpochUnlockEvent(startEpochId, this.planetId);
    }

    // [JsonIgnore]
    get EventId() {
        return Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.currentPlanetId).eventId;
    }

    // [JsonIgnore]
    get StartEpoch() {
        return this.epochs.length > 0 ? this.epochs[0] : 1;
    }

    get mergeProgressTarget() {
        let currentProgress = Core.Player.PlanetsSave.GetUpgradeProgress(
            UpgradeType.TapChallengeMergeBonus
        );
        let upgradeData = Core.GameData.Upgrades.UpgradeByType(UpgradeType.TapChallengeMergeBonus);
        return currentProgress.upgradeIndex > 0
            ? upgradeData.TotalValue(currentProgress.upgradeIndex) - (this.planetId - 1) * 5
            : 0;
    }

    get mergeProgressPercentage() {
        return Math.max(0, Math.min(1, this.mergeProgress / this.mergeProgressTarget));
    }

    Awake() {
        super.Awake();
        this.planetId = 0;
        this.creatures = []; //  List<int>;
        this.creaturesInBox = []; //  List<int>;
        this.creaturePurchases = []; //  List<CreaturesPurchaseSave>;
        this.collection = []; //  List<int>;
        this.epochs = []; //  List<int>;
        this.bubbles = 0;
        this.upgradesProgress = []; // List<UpgradeProgressData>
        this.mergeProgress = 0;
        this.mergeCount = 0;
        this.eventMoneySoft = 0n; // [JsonIgnore]
        this.eventMoneySoftStr = "0";
        this.availableCreaturesForPurchase = []; //new List<int>(); // [JsonIgnore]
    }

    fromJSON(json) {
        super.fromJSON(json);

        this.eventMoneySoft = BigInt(this.eventMoneySoftStr || 0);
        this.creaturePurchases = this.creaturePurchases.map(
            data => new CreaturesPurchaseSave(data)
        );
        this.upgradesProgress = this.upgradesProgress.map(data => new UpgradeProgressData(data));

        // у старых игроков, если они выполнили условия открытия бонуса для Тап Челенджа, должен прокачаться апгерйд на 1 раз
        if (Core.GameData.TapChallenge.isActive) {
            if (
                this.collection.includes(
                    Core.GameData.Upgrades.UpgradeByType(UpgradeType.TapChallengeMergeBonus)
                        .unlockCreatureId
                ) &&
                this.GetUpgradeProgress(UpgradeType.TapChallengeMergeBonus).upgradeIndex == 0
            ) {
                this.Upgrade(UpgradeType.TapChallengeMergeBonus);
            }
        }
    }

    toJSON() {
        return {
            planetId: this.planetId,
            creatures: this.creatures,
            creaturesInBox: this.creaturesInBox,
            creaturePurchases: this.creaturePurchases.map(obj => obj.toJSON()),
            collection: this.collection,
            epochs: this.epochs,
            bubbles: this.bubbles,
            upgradesProgress: this.upgradesProgress.map(obj => obj.toJSON()),
            mergeProgress: this.mergeProgress,
            mergeCount: this.mergeCount,
            eventMoneySoftStr: this.eventMoneySoft.toString()
        };
    }

    GetBankCreatureWithOffset(offset) {
        let availableForPurchaseCreatures = this.GetAvailableForPurchaseCreatures();
        let bonusCreatureId = 0;

        if (availableForPurchaseCreatures.length == 0) {
            bonusCreatureId = 1;
        } else {
            let indexWithOffset = Math.max(0, availableForPurchaseCreatures.length - 1 - offset);
            bonusCreatureId = availableForPurchaseCreatures[indexWithOffset];
        }
        return bonusCreatureId;
    }

    GetCollectionCreatureWithOffset(offset) {
        let collectionCreatures = this.collection;
        let bonusCreatureId = 0;
        if (collectionCreatures.length == 0) {
            bonusCreatureId = 1;
        } else {
            let indexWithOffset = Math.max(0, collectionCreatures.length - 1 - offset);
            bonusCreatureId = collectionCreatures[indexWithOffset];
        }
        return bonusCreatureId;
    }

    RemoveBoxCreature(id) {
        if (this.creaturesInBox) {
            let idx = this.creaturesInBox.indexOf(id);
            if (idx >= 0) this.creaturesInBox.splice(idx, 1);
            Core.Player.CallOnChanged();
        }
    }

    AddCreature(id, inBox = false) {
        this.creatures.push(id);
        let creatureData = Core.GameData.Creatures.Get(id);

        if (inBox) {
            if (!this.creaturesInBox) this.creaturesInBox = [];
            this.creaturesInBox.push(id);
        }

        if (!Core.Player.PlanetsSave.collection.includes(id)) {
            Core.Player.PlanetsSave.collection.push(id);
            Core.Player.CallOnNewCreatureInCollection(id);
            if (id >= Core.GameData.Config.starterPackCreatureId && Core.Time.Initialized) {
                if (Core.Player.Timers.starterPackTime == 0) {
                    Core.Player.Timers.starterPackTime = Core.Time.serverTime;
                    if (!PlayerPrefs.HasKey("starterpack")) {
                        Core.WaitBefore(0.5, () => {
                            PlayerPrefs.SetInt("starterpack", 1);
                            Core.UI.starterPackPopup.Show();
                        });
                    }
                }
            }
        }

        if (!this.collection.includes(id)) {
            this.collection.push(id);
            if (id != 1) {
                Core.UI.newCreature.Show(id);
            }
            if (Core.GameData.TapChallenge.isActive) {
                // Adding merge bonus to player if he adding new creature with unlockCreatureId to collection
                if (
                    Core.GameData.Upgrades.UpgradeByType(UpgradeType.TapChallengeMergeBonus)
                        .unlockCreatureId == id
                ) {
                    this.Upgrade(UpgradeType.TapChallengeMergeBonus);
                }
            }
            Core.Analytics.SendMonsterUnlockEvent(id, this.planetId, creatureData.creatureRank);
        }

        if (id <= 6) {
            let epoch = Core.GameData.Epochs.GetById(1);
            for (let i = epoch.creatures.length - 1; i >= 0; i--) {
                let c = epoch.creatures[i];
                if (c < id && !this.collection.includes(c)) {
                    this.collection.unshift(c);
                }
            }
        }
        Core.Player.CallOnChanged();
    }

    GetAvailableForPurchaseCreatures() {
        let list = this.availableCreaturesForPurchase || [];
        list.splice(0);

        let collectionSize = this.collection.length;
        if (collectionSize == 0) return list;

        let lastCreatureInCollection = this.collection[this.collection.length - 1];
        let openCreatureDistance = Core.GameData.Gameplay.creaturePurchaseDistance;
        if (collectionSize <= 4) {
            openCreatureDistance--;
        }
        for (let creature of this.collection) {
            let creatureIndexInCollection =
                Core.Player.PlanetsSave.CurrentPlanet.collection.indexOf(creature);
            if (creatureIndexInCollection + openCreatureDistance < collectionSize) {
                list.push(creature);
            }
        }
        if (list.length == 0 && this.planetId > 1) {
            list.push(1);
        }
        return list;
    }

    GetCreatureSoftPriceWithMultiplayer(creatureId) {
        let purchasesNumber = this.GetCreaturePurchasesSoft(creatureId);
        let creatureData = Core.GameData.Creatures.Get(creatureId);
        let planetData = Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.currentPlanetId);
        let basePrice = Core.GameData.Planets.basicCreaturePriceSoft;

        let price = new Price();
        price.priceType = MoneyType.Soft;

        let evBaseInt = parseInt(planetData.evolutionBaseSoft * 100);
        let a0 = Math.pow(100, creatureData.creatureRank);
        let a1 = Math.pow(evBaseInt, creatureData.creatureRank);

        let nBaseInt = parseInt(planetData.purchasesBaseSoft * 100);
        let b0 = Math.pow(100, purchasesNumber);
        let b1 = Math.pow(nBaseInt, purchasesNumber);

        price.priceValue =
            basePrice * planetData.pricesFactorSoft * BigInt(Math.floor((a1 / a0) * (b1 / b0)));

        if (Core.Boosters.IsCreaturesDiscountActive) {
            price.priceValue = (price.priceValue * 3n) / 4n;
            if (price.priceValue == 0n) price.priceValue = 1n;
        }
        return price;
    }

    GetCreatureHardPriceWithMultiplayer(creatureId) {
        let purchasesNumber = this.GetCreaturePurchasesHard(creatureId);
        let creatureData = Core.GameData.Creatures.Get(creatureId);
        let planetData = Core.GameData.Planets.GetPlanet(Core.Player.PlanetsSave.currentPlanetId);

        let price = new Price();
        price.priceType = MoneyType.Hard;

        price.priceValue =
            creatureData.basePriceHard +
            planetData.creaturePriceIncreaseHard +
            purchasesNumber / 10;

        if (Core.Boosters.IsCreaturesDiscountActive) {
            price.priceValue = (price.priceValue / 4) * 3;
            if (price.priceValue == 0n) price.priceValue = 1n;
        }

        return price;
    }

    GetCreaturePurchasesSoft(creatureId) {
        for (let c of this.creaturePurchases) {
            if (c.id == creatureId) return c.s;
        }
        return 0;
    }

    GetCreaturePurchasesHard(creatureId) {
        for (let c of this.creaturePurchases) {
            if (c.id == creatureId) return c.h;
        }
        return 0;
    }

    AddCreaturePurchase(creatureId, isSoft) {
        for (let c of this.creaturePurchases) {
            if (c.id == creatureId) {
                c[isSoft ? "s" : "h"]++;
                Core.Player.CallOnChanged();
                return;
            }
        }

        let p = new CreaturesPurchaseSave();
        p.id = creatureId;
        p[isSoft ? "s" : "h"] = 1;

        this.creaturePurchases.push(p);
        Core.Player.CallOnChanged();
    }

    AddEpoch(epoch) {
        // int epoch
        if (this.epochs.includes(epoch)) return;
        this.epochs.push(epoch);
        this.emit("EpochUnlocked", epoch);
        Core.Analytics.SendEpochUnlockEvent(epoch, this.planetId);
    }

    firstCreatureOffer(epoch) {
        let epochData = Core.GameData.Epochs.GetById(epoch);
        if (epochData.epochLevel < 3 || epochData.epochLevel > 4) return; // Wrong epoch
        if (
            !Core.Store.ProductAvailable("first_creature_offer_1") &&
            !Core.Store.ProductAvailable("first_creature_offer_2")
        )
            return;
        if (!Core.Time.Initialized) return;
        if (Core.Player.Timers.firstCreatureOfferTime) return; // Already offered

        Core.Player.Timers.firstCreatureOfferTime = Core.Time.serverTime;
        Core.UI.firstCreatureOffer.Show(epochData.creatures[0]);
    }

    GetUpgradeProgress(upgradeType) {
        for (let upgrade of this.upgradesProgress) {
            if (upgrade.upgradeType == upgradeType) {
                return upgrade;
            }
        }
        let emptyUpgrade = new UpgradeProgressData();
        emptyUpgrade.upgradeType = upgradeType;
        this.upgradesProgress.push(emptyUpgrade);
        Core.Player.CallOnChanged();
        return emptyUpgrade;
    }

    Upgrade(upgradeType, upgradeIndex = null) {
        let upgrade = this.upgradesProgress.find(upgrade => upgrade.upgradeType == upgradeType);
        if (!upgrade) {
            upgrade = new UpgradeProgressData();
            upgrade.upgradeType = upgradeType;
            upgrade.upgradeIndex = 0;
            this.upgradesProgress.push(upgrade);
        } else {
            Core.Analytics.SendUpgradeEvent(upgradeType, upgrade.upgradeIndex, this.planetId);
        }
        if (upgradeIndex === null) {
            upgradeIndex = upgrade.upgradeIndex + 1;
        }
        console.log("UPGRADE", UpgradeType[upgradeType], upgradeIndex);
        upgrade.upgradeIndex = upgradeIndex;
        Core.Player.CallOnChanged();
    }

    AddMergeProgress() {
        this.mergeCount++;
        let currentProgress = Core.Player.PlanetsSave.GetUpgradeProgress(
            UpgradeType.TapChallengeMergeBonus
        );
        if (currentProgress.upgradeIndex > 0) {
            this.mergeProgress++;
            if (this.mergeProgress >= this.mergeProgressTarget) {
                this.mergeProgress = 0;
                Core.Player.CallOnMergeBonus();
            }
            Core.Player.CallOnChanged();
        }
    }

    GetMergeProgress() {
        return this.mergeProgress;
    }

    GetMergeCount() {
        return this.mergeCount;
    }

    HasAnyUpgradesAvailable() {
        for (let u of Core.GameData.Upgrades.upgrades) {
            if (this.collection.includes(u.unlockCreatureId)) {
                return true;
            }
        }
        return false;
    }

    GetAvailableUpgradesNumber() {
        let number = 0;
        for (let u of Core.GameData.Upgrades.upgrades) {
            if (this.collection.includes(u.unlockCreatureId)) {
                number++;
            }
        }
        return number;
    }

    CheckTapChallengeStatus() {
        // internal void
        if (!Core.GameData.TapChallenge.isActive) return;
        if (
            !this.collection.includes(
                Core.GameData.Upgrades.UpgradeByType(UpgradeType.TapChallengeMergeBonus)
                    .unlockCreatureId
            )
        )
            return;
        if (this.GetUpgradeProgress(UpgradeType.TapChallengeMergeBonus).upgradeIndex == 0) {
            this.Upgrade(UpgradeType.TapChallengeMergeBonus);
        }
    }
}

export class UpgradeProgressData extends JSONGameData {
    Awake() {
        super.Awake();
        this.upgradeType = ""; // UpgradeType
        this.upgradeIndex = 0;
    }

    toJSON() {
        return {
            upgradeType: this.upgradeType,
            upgradeIndex: this.upgradeIndex
        };
    }
}

export class CreaturesPurchaseSave extends JSONGameData {
    Awake() {
        super.Awake();
        this.id = 0; // creature id
        this.s = 0; // soft purchases
        this.h = 0; // hard purchases
    }

    toJSON() {
        return {
            id: this.id,
            s: this.s,
            h: this.h
        };
    }
}
